/**
 * The Case filter URL, turned into the case-list endpoint's query params.
 *
 * One mapping feeds both the query key and the request, so a cached page can
 * never belong to a different filter than the one it is shown under. The API
 * is strict: an unknown or empty value, or a date it cannot parse, returns 400.
 * The URL schema already keeps the groups to API values; this drops what is
 * still unsafe to send.
 *
 * Every Case filter, and the search query, is a request param; nothing filters
 * loaded rows.
 */
import {
	CASE_FILTER_GROUPS,
	nonEmpty,
	visibleCaseFilterGroups,
} from './case-filter-groups'

import type { CaseFilters } from './case-filter-groups'
import type { CaseListSearch } from './case-filters'

/** The part of the Case list URL the request is built from. */
export type CaseFilterState = Pick<
	CaseListSearch,
	'filters' | 'openedAfter' | 'openedBefore' | 'q'
>

/** Every set group, with its URL values (`CASE_FILTER_GROUPS`), plus these. */
export type CaseListParams = CaseFilters & {
	/** Inclusive lower bound, ISO. Without it the API applies the last 30 days. */
	openedAfter?: string
	/** Upper bound, ISO. */
	openedBefore?: string
	/**
	 * Free text, trimmed. The API ranks Cases by embedding similarity over their
	 * metadata, so a Case can match on a field the list does not show. Results
	 * come most relevant first, ties by opened date.
	 */
	search?: string
}

/** Fewest non-space characters the search bar sends as a search. */
export const CASE_SEARCH_MIN_LENGTH = 3

/** How long the search bar waits after typing stops. Enter does not wait. */
export const CASE_SEARCH_DEBOUNCE_MS = 500

/** The query the request sends: trimmed, or `undefined` below the minimum. */
const toApiSearch = (q: string | undefined) => {
	const trimmed = q?.trim()
	return trimmed && trimmed.length >= CASE_SEARCH_MIN_LENGTH
		? trimmed
		: undefined
}

/**
 * The lower bound the request sends when the user chose none. Without
 * `openedAfter` the API applies the last 30 days, and nothing on screen would
 * say so. The API has no "no lower bound" param, so this stands in for one.
 */
export const ALL_TIME_START = '1970-01-01T00:00:00.000Z'

/** An ISO string for a parseable date; `undefined` for anything else. */
const toApiDate = (value: string | undefined) => {
	if (!value) return undefined
	const time = Date.parse(value)
	return Number.isNaN(time) ? undefined : new Date(time).toISOString()
}

export type ParamOptions = {
	/**
	 * Whether the Investigation filter is on screen. When it is not, a stale
	 * `investigation` param must not narrow the list with no visible reason.
	 */
	showInvestigation: boolean
}

/**
 * The request params for the Case filters and search in the URL — the query key
 * and the query string both come from this. Empty groups and unparseable dates
 * are left out, since the API answers either with 400; so is a search below
 * the minimum length. `openedAfter` is always sent: {@link ALL_TIME_START}
 * when the user chose no date.
 */
export const toCaseListParams = (
	{ filters, openedAfter, openedBefore, q }: CaseFilterState,
	{ showInvestigation }: ParamOptions,
): CaseListParams => {
	const params: CaseListParams = {
		...Object.fromEntries(
			visibleCaseFilterGroups(showInvestigation).map(({ key }) => [
				key,
				nonEmpty<string>(filters?.[key]),
			]),
		),
		openedAfter: toApiDate(openedAfter) ?? ALL_TIME_START,
		openedBefore: toApiDate(openedBefore),
		search: toApiSearch(q),
	}
	return Object.fromEntries(
		Object.entries(params).filter(([, value]) => value !== undefined),
	) as CaseListParams
}

/** Whether a date URL value is one the user chose: a parseable date. */
export const isChosenDate = (value: string | undefined) =>
	toApiDate(value) !== undefined

/**
 * How many filters the user set: one per filter value, one per date they chose.
 * A hidden Investigation group does not narrow the list, so it does not
 * count. The search query is not a filter. The
 * `Filters` button shows this number.
 */
export const countUserFilters = (
	{ filters, openedAfter, openedBefore }: CaseFilterState,
	{ showInvestigation }: ParamOptions,
) =>
	visibleCaseFilterGroups(showInvestigation).reduce(
		(count, { key }) => count + (filters?.[key]?.length ?? 0),
		0,
	) +
	(isChosenDate(openedAfter) ? 1 : 0) +
	(isChosenDate(openedBefore) ? 1 : 0)

/**
 * Whether the user narrowed the list — so an empty list without it is the
 * tenant having no Cases at all.
 */
export const hasUserFilters = (state: CaseFilterState, options: ParamOptions) =>
	countUserFilters(state, options) > 0

const GROUP_KEYS: ReadonlySet<string> = new Set(
	CASE_FILTER_GROUPS.map(({ key }) => key),
)

/**
 * The request's query string. A group repeats its param once per value; the
 * groups go in the order `params` holds them.
 */
export const caseListSearchParams = (
	limit: number,
	page: number,
	params: CaseListParams,
) => {
	const searchParams = new URLSearchParams({
		limit: limit.toString(),
		page: page.toString(),
	})

	// Walks `params`, not `CASE_FILTER_GROUPS`: the send order is the order the
	// caller built. Only a group key holds an array, so the cast is safe.
	for (const [key, values] of Object.entries(params)) {
		if (!GROUP_KEYS.has(key) || !Array.isArray(values)) continue
		for (const value of values as Array<string>) {
			// `source=` returns 400; the URL schema drops blanks, this is the guard.
			if (value.trim()) searchParams.append(key, value)
		}
	}
	if (params.openedAfter) searchParams.append('openedAfter', params.openedAfter)
	if (params.openedBefore) {
		searchParams.append('openedBefore', params.openedBefore)
	}
	// `search`, not the deprecated `metadata_search`; a blank one is no search.
	if (params.search?.trim()) searchParams.append('search', params.search)

	return searchParams
}
